import { useState, useEffect, useCallback } from 'react';
import { 
  aiProvidersApi, 
  AIProvider, 
  CreateProviderInput, 
  UpdateProviderInput 
} from '../api/ai.api';

export function useAIProviders() {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await aiProvidersApi.list();
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const createProvider = async (input: CreateProviderInput) => {
    const provider = await aiProvidersApi.create(input);
    setProviders(prev => [...prev, provider]);
    return provider;
  };

  const updateProvider = async (providerId: string, input: UpdateProviderInput) => {
    const provider = await aiProvidersApi.update(providerId, input);
    setProviders(prev => prev.map(p => p.id === providerId ? provider : p));
    return provider;
  };

  const deleteProvider = async (providerId: string) => {
    await aiProvidersApi.delete(providerId);
    setProviders(prev => prev.filter(p => p.id !== providerId));
  };

  const testProvider = async (providerId: string) => {
    return aiProvidersApi.test(providerId);
  };

  const listModels = async (providerId: string) => {
    return aiProvidersApi.listModels(providerId);
  };

  return {
    providers,
    loading,
    error,
    refetch: fetchProviders,
    createProvider,
    updateProvider,
    deleteProvider,
    testProvider,
    listModels,
  };
}
